/**
 * Listing radar: a forward paper test of "coins pump when Coinbase lists
 * them". Protocol: docs/BREAKOUT_WATCH_PLAN.md, Phase 2 (pre-registered
 * before this code was written).
 *
 * Every hour the scheduled function reads Coinbase Exchange's public product
 * list. A USD pair (not a stable pair) first seen after the seeding run and
 * after registration is a listing. Once a run sees it tradable, the paper
 * trade buys the open of the first hourly candle starting after that run and
 * sells the close of the 168th candle (7 days). Nothing here trades. Pure
 * logic: no network, no storage.
 */
import type { Candle } from '@whaletail/signal-engine';
import type { CoinbaseProduct } from './coinbaseBtc';
import {
  CRASH_BOUNCE_COSTS,
  clusterMeanInterval,
  netReturn,
  weekOf,
} from './crashBounce';

export const LISTING_RULE = {
  id: 'LR1',
  label: 'Listing radar: buy a new Coinbase USD pair at its first full hour, hold 7 days',
  quoteCurrency: 'USD',
  holdBars: 168,
  /** Close after this many candles is recorded but not judged. */
  checkpoints: [1, 24] as const,
  /** A trade still short of 168 candles this long after entry closes anyway. */
  maxWaitMs: 14 * 24 * 3_600_000,
  registeredAtMs: Date.parse('2026-10-15T00:00:00Z'),
} as const;

export interface ListingRuleParams {
  quoteCurrency: string;
  holdBars: number;
  maxWaitMs: number;
  registeredAtMs: number;
}

export const LISTING_COSTS = CRASH_BOUNCE_COSTS;

/** Set before any forward data existed (Phase 2 protocol). */
export const LISTING_PASS_BAR = {
  minListings: 20,
  minWeeks: 8,
  feePerSide: LISTING_COSTS.feeHigh,
  minLowerBound: 0,
} as const;

export const LISTING_STAKE_DOLLARS = 1_000;

const HOUR_MS = 3_600_000;

export interface Listing {
  productId: string;
  base: string;
  /** Run time that first saw the product. */
  firstSeenMs: number;
  /** Run time that first saw it tradable; null while only announced. */
  tradableSeenMs: number | null;
  /** Start of the entry candle; set once a candle at/after the target hour exists. */
  entryMs: number | null;
  /** Entry open incl. slippage. */
  entryPx: number | null;
  /** Candles seen from entry on (entry candle = 1). */
  bars: number;
  lastBarMs: number | null;
  lastClose: number | null;
  maxHigh: number | null;
  minLow: number | null;
  /** Close after 1 and 24 candles (raw, no slippage). */
  close1: number | null;
  close24: number | null;
  /** Set when the trade closes: exit fill incl. slippage. */
  exitMs: number | null;
  exitPx: number | null;
  /** True when it closed on the 14-day timeout instead of 168 candles. */
  incomplete: boolean;
}

export interface ListingRadarState {
  schemaVersion: 1;
  /** Run time of the seeding run; null before the first run. */
  seededAtMs: number | null;
  /** Every product id ever seen, so a product that comes back isn't new. */
  known: string[];
  /** Listings not yet closed: announced or open. */
  active: Listing[];
  /** Closed listings, oldest first. */
  closed: Listing[];
  lastRunMs: number | null;
  lastError: string | null;
}

export const MAX_LISTING_RECORDS = 5_000;

export function emptyListingRadarState(): ListingRadarState {
  return {
    schemaVersion: 1,
    seededAtMs: null,
    known: [],
    active: [],
    closed: [],
    lastRunMs: null,
    lastError: null,
  };
}

export function isEligible(p: CoinbaseProduct, rule: ListingRuleParams = LISTING_RULE) {
  return p.quoteCurrency === rule.quoteCurrency && !p.fxStablecoin;
}

/** Orders can match: online and not disabled, cancel-only, post-only or in auction. */
export function isTradable(p: CoinbaseProduct) {
  return (
    p.status === 'online' &&
    !p.tradingDisabled &&
    !p.cancelOnly &&
    !p.postOnly &&
    !p.auctionMode
  );
}

function newListing(p: CoinbaseProduct, nowMs: number): Listing {
  return {
    productId: p.id,
    base: p.baseCurrency,
    firstSeenMs: nowMs,
    tradableSeenMs: null,
    entryMs: null,
    entryPx: null,
    bars: 0,
    lastBarMs: null,
    lastClose: null,
    maxHigh: null,
    minLow: null,
    close1: null,
    close24: null,
    exitMs: null,
    exitPx: null,
    incomplete: false,
  };
}

/**
 * Applies one run's product list. Seeds on the first run (nothing is a
 * listing), records new eligible products as listings, and stamps the first
 * run that sees an announced listing tradable. Returns the new state and the
 * listings that became tradable on this run.
 */
export function applyProducts(
  prev: ListingRadarState,
  products: readonly CoinbaseProduct[],
  nowMs: number,
  rule: ListingRuleParams = LISTING_RULE,
): { state: ListingRadarState; newlyTradable: Listing[]; newlyAnnounced: Listing[] } {
  const state: ListingRadarState = {
    ...prev,
    known: [...prev.known],
    active: prev.active.map((l) => ({ ...l })),
  };
  const newlyTradable: Listing[] = [];
  const newlyAnnounced: Listing[] = [];
  const byId = new Map(products.map((p) => [p.id, p]));

  if (state.seededAtMs === null) {
    state.seededAtMs = nowMs;
    state.known = [...byId.keys()].sort();
    return { state, newlyTradable, newlyAnnounced };
  }

  const known = new Set(state.known);
  for (const p of products) {
    if (known.has(p.id)) continue;
    known.add(p.id);
    if (!isEligible(p, rule) || nowMs < rule.registeredAtMs) continue;
    const l = newListing(p, nowMs);
    state.active.push(l);
    newlyAnnounced.push(l);
  }
  state.known = [...known].sort();

  for (const l of state.active) {
    if (l.tradableSeenMs !== null) continue;
    const p = byId.get(l.productId);
    if (p && isTradable(p)) {
      l.tradableSeenMs = nowMs;
      newlyTradable.push(l);
    }
  }
  return { state, newlyTradable, newlyAnnounced };
}

/** Start of the first hour that begins after `ms`. */
export const nextHourStart = (ms: number) => Math.floor(ms / HOUR_MS) * HOUR_MS + HOUR_MS;

/**
 * Advances one tradable listing through its newly completed candles. Entry is
 * the first candle starting at or after the hour after `tradableSeenMs`.
 * Returns the updated listing; `exitMs` is set when it closes.
 */
export function stepListing(
  prev: Listing,
  candles: readonly Candle[],
  nowMs: number,
  rule: ListingRuleParams = LISTING_RULE,
  slippage: number = LISTING_COSTS.slippagePerSide,
): Listing {
  const l: Listing = { ...prev };
  if (l.tradableSeenMs === null || l.exitMs !== null) return l;
  const earliestEntry = nextHourStart(l.tradableSeenMs);

  for (const c of candles) {
    const tMs = c.time * 1000;
    if (tMs + HOUR_MS > nowMs) break; // still forming
    if (tMs < earliestEntry) continue;
    if (l.lastBarMs !== null && tMs <= l.lastBarMs) continue;

    if (l.entryMs === null) {
      l.entryMs = tMs;
      l.entryPx = c.open * (1 + slippage);
    }
    l.bars += 1;
    l.lastBarMs = tMs;
    l.lastClose = c.close;
    l.maxHigh = l.maxHigh === null ? c.high : Math.max(l.maxHigh, c.high);
    l.minLow = l.minLow === null ? c.low : Math.min(l.minLow, c.low);
    if (l.bars === 1) l.close1 = c.close;
    if (l.bars === 24) l.close24 = c.close;
    if (l.bars >= rule.holdBars) {
      l.exitMs = tMs;
      l.exitPx = c.close * (1 - slippage);
      return l;
    }
  }

  // Delisted or halted: close at the last known price once the wait is over.
  if (l.entryMs !== null && nowMs >= l.entryMs + rule.maxWaitMs && l.lastClose !== null) {
    l.exitMs = l.lastBarMs;
    l.exitPx = l.lastClose * (1 - slippage);
    l.incomplete = true;
  }
  // Never traded at all: give up without a price, so it can't count either way.
  if (l.entryMs === null && nowMs >= earliestEntry + rule.maxWaitMs) {
    l.exitMs = nowMs;
    l.incomplete = true;
  }
  return l;
}

// ---- parsing ----

const isNum = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);
const numOrNull = (x: unknown) => (isNum(x) ? x : null);

function parseListing(raw: unknown): Listing | null {
  const l = raw as Partial<Listing> | null;
  if (!l || typeof l.productId !== 'string' || typeof l.base !== 'string') return null;
  if (!isNum(l.firstSeenMs) || !isNum(l.bars)) return null;
  return {
    productId: l.productId,
    base: l.base,
    firstSeenMs: l.firstSeenMs,
    tradableSeenMs: numOrNull(l.tradableSeenMs),
    entryMs: numOrNull(l.entryMs),
    entryPx: numOrNull(l.entryPx),
    bars: l.bars,
    lastBarMs: numOrNull(l.lastBarMs),
    lastClose: numOrNull(l.lastClose),
    maxHigh: numOrNull(l.maxHigh),
    minLow: numOrNull(l.minLow),
    close1: numOrNull(l.close1),
    close24: numOrNull(l.close24),
    exitMs: numOrNull(l.exitMs),
    exitPx: numOrNull(l.exitPx),
    incomplete: l.incomplete === true,
  };
}

export function parseListingRadarState(raw: string): ListingRadarState {
  try {
    const p = JSON.parse(raw) as Partial<ListingRadarState>;
    if (p?.schemaVersion !== 1) return emptyListingRadarState();
    const list = (x: unknown) =>
      (Array.isArray(x) ? x : [])
        .map(parseListing)
        .filter((l): l is Listing => l !== null);
    return {
      schemaVersion: 1,
      seededAtMs: numOrNull(p.seededAtMs),
      known: Array.isArray(p.known)
        ? p.known.filter((x): x is string => typeof x === 'string')
        : [],
      active: list(p.active),
      closed: list(p.closed).filter((l) => l.exitPx !== null && l.entryPx !== null),
      lastRunMs: numOrNull(p.lastRunMs),
      lastError: typeof p.lastError === 'string' ? p.lastError : null,
    };
  } catch {
    return emptyListingRadarState();
  }
}

// ---- summary ----

export type ListingVerdict = 'collecting' | 'passing' | 'failing';

export interface ListingSummary {
  listings: number;
  weeks: number;
  announced: number;
  open: number;
  winRate: number | null;
  meanNetLow: number | null;
  meanNetHigh: number | null;
  intervalHigh: [number, number] | null;
  pnlHigh: number;
  /** Raw mean return to the close after 1 and 24 candles (not judged). */
  mean1h: number | null;
  mean24h: number | null;
  verdict: ListingVerdict;
}

export function summarizeListingRadar(state: ListingRadarState): ListingSummary {
  const closed = state.closed.filter((l) => l.entryPx !== null && l.exitPx !== null);
  const low = closed.map((l) => netReturn(l.entryPx!, l.exitPx!, LISTING_COSTS.feeLow));
  const high = closed.map((l) => netReturn(l.entryPx!, l.exitPx!, LISTING_COSTS.feeHigh));
  const weeks = new Set(closed.map((l) => weekOf(l.entryMs!))).size;
  const mean = (a: number[]) =>
    a.length ? a.reduce((x, y) => x + y, 0) / a.length : null;
  const meanHigh = mean(high);
  const interval = clusterMeanInterval(
    high,
    closed.map((l) => weekOf(l.entryMs!)),
  );
  const checkpoint = (pick: (l: Listing) => number | null) =>
    mean(closed.filter((l) => pick(l) !== null).map((l) => pick(l)! / l.entryPx! - 1));

  const bar = LISTING_PASS_BAR;
  let verdict: ListingVerdict = 'collecting';
  if (closed.length >= bar.minListings && weeks >= bar.minWeeks) {
    verdict =
      meanHigh !== null &&
      meanHigh > 0 &&
      interval !== null &&
      interval[0] > bar.minLowerBound
        ? 'passing'
        : 'failing';
  }
  return {
    listings: closed.length,
    weeks,
    announced: state.active.filter((l) => l.tradableSeenMs === null).length,
    open: state.active.filter((l) => l.tradableSeenMs !== null).length,
    winRate: closed.length ? high.filter((x) => x > 0).length / closed.length : null,
    meanNetLow: mean(low),
    meanNetHigh: meanHigh,
    intervalHigh: interval,
    pnlHigh: high.reduce((a, b) => a + b, 0) * LISTING_STAKE_DOLLARS,
    mean1h: checkpoint((l) => l.close1),
    mean24h: checkpoint((l) => l.close24),
    verdict,
  };
}
